import { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ShieldCheck, TriangleAlert } from 'lucide-react';
import { ApiClientError, LogoMark, Spinner } from '@riskpay/ui-components';
import { authApi, useAuth } from '../lib/client';
import { CopyRow, EmailVerification, type Session } from './EmailVerification';

/* ==========================================================================
 *  Merchant sign-up
 *
 *  Everything the API needs is collected on one screen — splitting six fields
 *  across a wizard adds ceremony without adding clarity — and then the address
 *  is confirmed before anything else happens.
 *
 *  The keys are NOT handed over here any more. They arrive at the end of
 *  confirmation, because an unconfirmed address is only a string somebody
 *  typed: a typo, or somebody else's inbox, would otherwise walk away with a
 *  working sandbox credential and a dashboard.
 *
 *  KYC is still not collected here. Test keys are issued as soon as the email
 *  is confirmed so engineering can integrate while compliance review runs;
 *  asking for a Board Resolution before someone has seen the dashboard is how
 *  you lose the signup.
 * ========================================================================== */

const ENTITY_TYPES = [
  ['PROPRIETORSHIP', 'Proprietorship / Individual'],
  ['PARTNERSHIP', 'Partnership Firm'],
  ['LLP', 'Limited Liability Partnership'],
  ['PRIVATE_LIMITED', 'Private Limited Company'],
  ['PUBLIC_LIMITED', 'Public Limited Company'],
] as const;

const INDUSTRIES = [
  ['ECOMMERCE', 'E-commerce'],
  ['ELECTRONICS', 'Electronics'],
  ['DIGITAL_SERVICES', 'Digital Services'],
  ['SAAS', 'SaaS'],
  ['SUBSCRIPTION', 'Subscription'],
  ['TRAVEL', 'Travel'],
  ['EDUCATION', 'Education'],
  ['MARKETPLACE', 'Marketplace'],
  ['PROFESSIONAL_SERVICES', 'Professional Services'],
  ['GAMING_SKILL_BASED', 'Gaming & Skill-Based'],
  ['OTHER', 'Other'],
] as const;

interface SignupResponse {
  merchant_id: string;
  kyc_status: string;
  message: string;
  /** False only under BYPASS_OTP, where no passcode is ever issued. */
  verification_required: boolean;
  email?: string;
  resend_after?: number;
  dev_code?: string;
  /** Present only on the BYPASS_OTP path — otherwise the keys come later. */
  test_credentials?: { public_key: string; secret_key: string; note: string };
}

/** Mirrors the server's password policy so the rules are visible while typing. */
function passwordChecks(value: string) {
  return [
    { label: 'At least 12 characters', ok: value.length >= 12 },
    { label: 'An uppercase letter', ok: /[A-Z]/.test(value) },
    { label: 'A lowercase letter', ok: /[a-z]/.test(value) },
    { label: 'A digit', ok: /\d/.test(value) },
  ];
}

function Field({
  label,
  name,
  hint,
  ...rest
}: { label: string; name: string; hint?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label htmlFor={name} className="label">
        {label}
      </label>
      <input id={name} name={name} className="input" {...rest} />
      {hint && <p className="mt-1.5 text-2xs text-content-faint">{hint}</p>}
    </div>
  );
}

export function SignUp({ onNavigate }: { onNavigate: (to: 'signin') => void }) {
  const signIn = useAuth((st) => st.signIn);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [param, setParam] = useState<string | null>(null);
  const [created, setCreated] = useState<SignupResponse | null>(null);
  const [email, setEmail] = useState('');

  const checks = useMemo(() => passwordChecks(password), [password]);
  const passwordOk = checks.every((c) => c.ok);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setParam(null);

    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries()) as Record<string, string>;
    // The API rejects an empty string for an optional URL field.
    if (!payload.website_url) delete payload.website_url;
    if (!payload.phone) delete payload.phone;

    try {
      const result = await authApi.post<SignupResponse>('/merchant/signup', payload);
      setEmail(payload.email ?? '');
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
        setParam(err.param ?? null);
      } else {
        setError('Could not create the account. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  /* ---- Step 2: confirm the address ------------------------------------ */
  if (created?.verification_required) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 py-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 flex items-center gap-2.5">
            <LogoMark size={52} />
            <div className="leading-none">
              <p className="text-[15px] font-semibold tracking-tight">RiskPay</p>
              <p className="mt-1 text-2xs text-content-subtle">Merchant Portal</p>
            </div>
          </div>

          <EmailVerification
            email={email}
            merchantRef={created.merchant_id}
            resendAfter={created.resend_after ?? 60}
            initialDevCode={created.dev_code ?? null}
            onVerified={(session: Session) =>
              signIn({ accessToken: session.access_token, user: session.user, mode: 'TEST' })
            }
          />
        </div>
      </div>
    );
  }

  /* ---- Step 2b: BYPASS_OTP — no code was ever sent --------------------- */
  if (created) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 py-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 flex items-center gap-2.5">
            <LogoMark size={52} />
            <div className="leading-none">
              <p className="text-[15px] font-semibold tracking-tight">RiskPay</p>
              <p className="mt-1 text-2xs text-content-subtle">Merchant Portal</p>
            </div>
          </div>

          <div className="panel p-6 sm:p-7">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
              <Check className="h-4 w-4 text-brand-400" aria-hidden="true" />
            </span>
            <h1 className="mt-5 text-lg font-semibold tracking-tight">Account created</h1>
            <p className="mt-2 text-sm leading-relaxed text-content-muted">
              Your merchant reference is <span className="numeral text-content">{created.merchant_id}</span>. Email
              confirmation is switched off on this environment.
            </p>

            {created.test_credentials && (
              <>
                <div className="mt-6 rounded-md border border-warning-ring bg-warning-soft p-4">
                  <p className="flex items-start gap-2 text-xs leading-relaxed text-[#F5C46B]">
                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    This is the only time the secret key is shown. Copy it into your secret manager now — we store only
                    a hash and cannot recover it.
                  </p>
                </div>

                <div className="mt-5 space-y-4">
                  <CopyRow label="Publishable key" value={created.test_credentials.public_key} />
                  <CopyRow label="Secret key (test mode)" value={created.test_credentials.secret_key} />
                </div>
              </>
            )}

            <button type="button" onClick={() => onNavigate('signin')} className="btn btn-primary btn-lg mt-7 w-full">
              Continue to sign in
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- Step 1: the form ------------------------------------------------ */
  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center gap-2.5">
          <LogoMark size={52} />
          <div className="leading-none">
            <p className="text-[15px] font-semibold tracking-tight">RiskPay</p>
            <p className="mt-1 text-2xs text-content-subtle">Merchant Portal</p>
          </div>
        </div>

        <div className="panel p-6 sm:p-7">
          <h1 className="text-lg font-semibold tracking-tight">Create your account</h1>
          <p className="mt-1.5 text-xs leading-relaxed text-content-muted">
            Test API keys are issued immediately. You do not need to complete KYC before you start integrating.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <p className="meta mb-3">About you</p>
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" name="full_name" autoComplete="name" required minLength={2} />
                  <Field label="Phone" name="phone" type="tel" autoComplete="tel" placeholder="+91" />
                </div>
                <Field
                  label="Work email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  aria-invalid={param === 'email'}
                  hint="Verification and settlement notices go here."
                />

                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    aria-invalid={param === 'password'}
                  />
                  <ul className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {checks.map((c) => (
                      <li
                        key={c.label}
                        className={`flex items-center gap-1.5 text-2xs ${c.ok ? 'text-brand-400' : 'text-content-faint'}`}
                      >
                        <Check className={`h-3 w-3 shrink-0 ${c.ok ? '' : 'opacity-30'}`} aria-hidden="true" />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-line pt-5">
              <p className="meta mb-3">About the business</p>
              <div className="space-y-4">
                <Field
                  label="Registered legal name"
                  name="legal_name"
                  required
                  minLength={2}
                  hint="Exactly as it appears on your PAN or incorporation certificate."
                />
                <Field
                  label="Trading name"
                  name="display_name"
                  required
                  minLength={2}
                  hint="What customers see on the payment page and their bank statement."
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="entity_type" className="label">
                      Entity type
                    </label>
                    <select id="entity_type" name="entity_type" className="input" required defaultValue="">
                      <option value="" disabled>
                        Select…
                      </option>
                      {ENTITY_TYPES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="industry_category" className="label">
                      Industry
                    </label>
                    <select id="industry_category" name="industry_category" className="input" defaultValue="ECOMMERCE">
                      {INDUSTRIES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Field label="Website" name="website_url" type="url" placeholder="https://" />
              </div>
            </div>

            {error && (
              <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-danger-text">
                {error}
              </p>
            )}

            <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy || !passwordOk}>
              {busy ? <Spinner /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
              {busy ? 'Creating account' : 'Create account'}
            </button>

            <p className="flex items-start gap-2 text-2xs leading-relaxed text-content-faint">
              <ShieldCheck className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              By creating an account you agree to our Terms of Service and Privacy Policy. Eligibility remains subject
              to compliance review and acquirer policy.
            </p>
          </form>

          <div className="mt-6 border-t border-line pt-4">
            <button type="button" onClick={() => onNavigate('signin')} className="btn btn-ghost btn-sm -ml-2">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
